// src/pages/profile-settings/doctor/practiceTeamWorkspaceModel.js
import { PRACTICE_TEAM_ROLES } from "./practiceTeamConstants.js";

export const PRACTICE_TEAM_CAPABILITY_STATES = {
  AVAILABLE: "available",
  LOCKED: "locked",
  PENDING_VERIFICATION: "pending_verification",
  PERMISSION_DENIED: "permission_denied",
};

export const PRACTICE_TEAM_ACTIONS = [
  "view",
  "create",
  "edit",
  "delete",
  "approve",
  "export",
  "invite",
  "configure",
];

export const PRACTICE_TEAM_CAPABILITIES = {
  WORKSPACE_HOME_VIEW: "workspace.home.view",
  TASKS_VIEW: "tasks.view",
  APPOINTMENTS_VIEW: "appointments.view",
  APPOINTMENTS_CREATE: "appointments.create",
  APPOINTMENTS_EDIT: "appointments.edit",
  MESSAGES_VIEW_ADMIN: "messages.admin.view",
  MESSAGES_CREATE_ADMIN: "messages.admin.create",
  PATIENT_CONTACT_VIEW_LIMITED: "patients.contact.view_limited",
  BILLING_STATUS_VIEW: "billing.status.view",
  BILLING_FOLLOW_UP: "billing.follow_up",
  PRACTICE_OPERATIONS_VIEW: "practice.operations.view",
  PRACTICE_OPERATIONS_EDIT: "practice.operations.edit",
  TEAM_INVITE_LIMITED: "team.invite_limited",
  TEAM_CONFIGURE_LIMITED: "team.configure_limited",
  MANAGE_PRACTICE_TEAM: "manage_practice_team",
  NOTIFICATIONS_VIEW: "notifications.view",
};

export const PRACTICE_TEAM_PERMISSION_GROUPS = {
  workspace: {
    label: "Workspace",
    capabilities: [
      PRACTICE_TEAM_CAPABILITIES.WORKSPACE_HOME_VIEW,
      PRACTICE_TEAM_CAPABILITIES.TASKS_VIEW,
      PRACTICE_TEAM_CAPABILITIES.NOTIFICATIONS_VIEW,
    ],
  },
  appointments: {
    label: "Appointments",
    capabilities: [
      PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_VIEW,
      PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_CREATE,
      PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_EDIT,
    ],
  },
  communications: {
    label: "Messages",
    capabilities: [
      PRACTICE_TEAM_CAPABILITIES.MESSAGES_VIEW_ADMIN,
      PRACTICE_TEAM_CAPABILITIES.MESSAGES_CREATE_ADMIN,
    ],
  },
  patients: {
    label: "Patients",
    capabilities: [PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED],
  },
  billing: {
    label: "Billing",
    capabilities: [
      PRACTICE_TEAM_CAPABILITIES.BILLING_STATUS_VIEW,
      PRACTICE_TEAM_CAPABILITIES.BILLING_FOLLOW_UP,
    ],
  },
  practice: {
    label: "Practice",
    capabilities: [
      PRACTICE_TEAM_CAPABILITIES.PRACTICE_OPERATIONS_VIEW,
      PRACTICE_TEAM_CAPABILITIES.PRACTICE_OPERATIONS_EDIT,
      PRACTICE_TEAM_CAPABILITIES.TEAM_INVITE_LIMITED,
      PRACTICE_TEAM_CAPABILITIES.TEAM_CONFIGURE_LIMITED,
      PRACTICE_TEAM_CAPABILITIES.MANAGE_PRACTICE_TEAM,
    ],
  },
};

const ROLE_SEQUENCE = [
  "practice_assistant",
  "receptionist",
  "practice_manager",
  "billing_administrator",
];

const ROLE_CAPABILITIES = {
  practice_assistant: [
    PRACTICE_TEAM_CAPABILITIES.WORKSPACE_HOME_VIEW,
    PRACTICE_TEAM_CAPABILITIES.TASKS_VIEW,
    PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_VIEW,
    PRACTICE_TEAM_CAPABILITIES.NOTIFICATIONS_VIEW,
  ],
  receptionist: [
    PRACTICE_TEAM_CAPABILITIES.WORKSPACE_HOME_VIEW,
    PRACTICE_TEAM_CAPABILITIES.TASKS_VIEW,
    PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_VIEW,
    PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_CREATE,
    PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_EDIT,
    PRACTICE_TEAM_CAPABILITIES.MESSAGES_VIEW_ADMIN,
    PRACTICE_TEAM_CAPABILITIES.MESSAGES_CREATE_ADMIN,
    PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED,
    PRACTICE_TEAM_CAPABILITIES.NOTIFICATIONS_VIEW,
  ],
  practice_manager: [
    PRACTICE_TEAM_CAPABILITIES.WORKSPACE_HOME_VIEW,
    PRACTICE_TEAM_CAPABILITIES.TASKS_VIEW,
    PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_VIEW,
    PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_CREATE,
    PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_EDIT,
    PRACTICE_TEAM_CAPABILITIES.MESSAGES_VIEW_ADMIN,
    PRACTICE_TEAM_CAPABILITIES.MESSAGES_CREATE_ADMIN,
    PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED,
    PRACTICE_TEAM_CAPABILITIES.PRACTICE_OPERATIONS_VIEW,
    PRACTICE_TEAM_CAPABILITIES.PRACTICE_OPERATIONS_EDIT,
    PRACTICE_TEAM_CAPABILITIES.TEAM_INVITE_LIMITED,
    PRACTICE_TEAM_CAPABILITIES.TEAM_CONFIGURE_LIMITED,
    PRACTICE_TEAM_CAPABILITIES.MANAGE_PRACTICE_TEAM,
    PRACTICE_TEAM_CAPABILITIES.NOTIFICATIONS_VIEW,
  ],
  billing_administrator: [
    PRACTICE_TEAM_CAPABILITIES.WORKSPACE_HOME_VIEW,
    PRACTICE_TEAM_CAPABILITIES.TASKS_VIEW,
    PRACTICE_TEAM_CAPABILITIES.BILLING_STATUS_VIEW,
    PRACTICE_TEAM_CAPABILITIES.BILLING_FOLLOW_UP,
    PRACTICE_TEAM_CAPABILITIES.NOTIFICATIONS_VIEW,
  ],
};

export const PRACTICE_TEAM_NAVIGATION_SECTIONS = [
  {
    id: "home",
    label: "Home",
    path: "/practice-team/welcome",
    capability: PRACTICE_TEAM_CAPABILITIES.WORKSPACE_HOME_VIEW,
    routeImplemented: true,
  },
  {
    id: "tasks",
    label: "My Tasks",
    path: "/practice-team/tasks",
    capability: PRACTICE_TEAM_CAPABILITIES.TASKS_VIEW,
    routeImplemented: true,
  },
  {
    id: "appointments",
    label: "Appointments",
    path: "/practice-team/appointments",
    capability: PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_VIEW,
    routeImplemented: true,
  },
  {
    id: "messages",
    label: "Messages",
    path: "/practice-team/messages",
    capability: PRACTICE_TEAM_CAPABILITIES.MESSAGES_VIEW_ADMIN,
    routeImplemented: true,
  },
  {
    id: "patients",
    label: "Patients",
    path: "/practice-team/patients",
    capability: PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED,
    routeImplemented: true,
  },
  {
    id: "billing",
    label: "Billing",
    path: "/practice-team/billing",
    capability: PRACTICE_TEAM_CAPABILITIES.BILLING_STATUS_VIEW,
    routeImplemented: false,
  },
  {
    id: "practice",
    label: "Practice",
    path: "/practice-team/practice",
    capability: PRACTICE_TEAM_CAPABILITIES.PRACTICE_OPERATIONS_VIEW,
    routeImplemented: false,
  },
  {
    id: "notifications",
    label: "Notifications",
    path: "/practice-team/notifications",
    capability: PRACTICE_TEAM_CAPABILITIES.NOTIFICATIONS_VIEW,
    routeImplemented: false,
  },
];

export const PRACTICE_TEAM_TASK_GUIDE = {
  practice_assistant: [
    "Review assigned practice relationship",
    "Confirm role and access status",
    "Prepare for day-to-day appointment administration",
  ],
  receptionist: [
    "Review today's bookings",
    "Prepare appointment confirmations",
    "Prepare administrative patient communication",
  ],
  practice_manager: [
    "Review practice operations",
    "Prepare team coordination tasks",
    "Prepare administrative follow-up workflows",
  ],
  billing_administrator: [
    "Review billing status placeholders",
    "Prepare payment follow-up workflows",
    "Prepare reconciliation tasks without payout access",
  ],
};

export const PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES = {
  CRITICAL: "critical",
  HIGH: "high",
  NORMAL: "normal",
  LOW: "low",
};

export const PRACTICE_TEAM_DELEGATED_TASK_STATUSES = {
  OPEN: "open",
  SCHEDULED: "scheduled",
  COMPLETED: "completed",
};

export const PRACTICE_TEAM_DELEGATED_MESSAGE_STATUSES = {
  NEW: "new",
  FOLLOW_UP: "follow-up",
  INTERNAL: "internal",
  ARCHIVED: "archived",
};

export const PRACTICE_TEAM_PATIENT_COORDINATION_STATUSES = {
  NEW_REGISTRATION: "new registration",
  AWAITING_VERIFICATION: "awaiting verification",
  FOLLOW_UP: "follow-up required",
  INCOMPLETE_PROFILE: "incomplete profile",
  READY_FOR_BOOKING: "booking preparation",
};

export const PRACTICE_TEAM_DELEGATED_TASK_SECTIONS = [
  {
    id: "today",
    label: "Today",
    description: "Tasks requiring immediate attention.",
    emptyTitle: "No tasks due today.",
    emptyBody: "Immediate delegated work will appear here when enabled by your practice.",
  },
  {
    id: "upcoming",
    label: "Upcoming",
    description: "Future delegated work.",
    emptyTitle: "No upcoming delegated work.",
    emptyBody: "Scheduled follow-up tasks will appear here when delegated workflows are enabled.",
  },
  {
    id: "completed",
    label: "Completed",
    description: "Recently completed delegated work.",
    emptyTitle: "No completed delegated tasks yet.",
    emptyBody: "Recently completed delegated work will appear here for review.",
  },
];

export const PRACTICE_TEAM_DELEGATED_MESSAGE_SECTIONS = [
  {
    id: "new_patient_messages",
    label: "New patient messages",
    description: "Patient communication will appear here once delegated messaging is enabled.",
    emptyTitle: "No new patient messages.",
    emptyBody: "Delegated patient communication will appear here when your practice enables messaging workflows.",
  },
  {
    id: "follow_up_required",
    label: "Follow-up required",
    description: "Messages that need administrative follow-up will appear here.",
    emptyTitle: "No message follow-up due.",
    emptyBody: "Follow-up work will appear here when delegated messaging workflows are enabled.",
  },
  {
    id: "internal_practice_notes",
    label: "Internal practice notes",
    description: "Internal operational notes for the assigned practice will appear here.",
    emptyTitle: "No internal practice notes.",
    emptyBody: "Internal notes will appear here when practice coordination workflows are enabled.",
  },
  {
    id: "completed_archived",
    label: "Completed / archived",
    description: "Recently closed delegated message work will appear here.",
    emptyTitle: "No archived message work yet.",
    emptyBody: "Closed messaging work will appear here for review once enabled.",
  },
];

export const PRACTICE_TEAM_PATIENT_COORDINATION_SECTIONS = [
  {
    id: "new_registrations",
    label: "New registrations",
    description: "Registration coordination placeholders will appear here once enabled.",
    emptyTitle: "No new registrations.",
    emptyBody: "New patient registration coordination will appear here when delegated workflows are enabled.",
  },
  {
    id: "awaiting_verification",
    label: "Awaiting verification",
    description: "Non-clinical verification readiness work will appear here.",
    emptyTitle: "No verification follow-up.",
    emptyBody: "Verification readiness work will appear here once the practice enables patient coordination workflows.",
  },
  {
    id: "follow_up_required",
    label: "Follow-up required",
    description: "Administrative patient follow-up placeholders will appear here.",
    emptyTitle: "No patient follow-up due.",
    emptyBody: "Follow-up coordination will appear here when delegated patient workflows are enabled.",
  },
  {
    id: "booking_preparation",
    label: "Booking preparation",
    description: "Booking preparation work will appear here without exposing clinical records.",
    emptyTitle: "No booking preparation items.",
    emptyBody: "Booking preparation work will appear here once delegated scheduling workflows are enabled.",
  },
  {
    id: "completed_archived",
    label: "Completed / archived",
    description: "Recently closed patient coordination work will appear here.",
    emptyTitle: "No archived patient coordination work.",
    emptyBody: "Closed patient coordination work will appear here for review once enabled.",
  },
];

export const PRACTICE_TEAM_PRIORITY_META = {
  [PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.CRITICAL]: {
    label: "Critical",
    dotClassName: "bg-[rgb(var(--schemes-critical))]",
    chipClassName: "bg-[var(--state-critical-12)] text-[rgb(var(--schemes-critical))]",
  },
  [PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.HIGH]: {
    label: "High",
    dotClassName: "bg-[rgb(var(--afrimed-primary))]",
    chipClassName: "bg-[var(--state-primary-16)] text-primary",
  },
  [PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.NORMAL]: {
    label: "Normal",
    dotClassName: "bg-[rgb(var(--schemes-success))]",
    chipClassName: "bg-[var(--state-success-12)] text-[rgb(var(--schemes-success))]",
  },
  [PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.LOW]: {
    label: "Low",
    dotClassName: "bg-[rgb(var(--clr-on-surface-variant))]",
    chipClassName: "bg-[rgba(var(--clr-on-surface),0.08)] text-on-surface-var",
  },
};

export const PRACTICE_TEAM_DELEGATED_TASK_TYPES = {
  APPOINTMENT_COORDINATION: "appointment_coordination",
  PATIENT_COMMUNICATION: "patient_communication",
  BILLING_FOLLOW_UP: "billing_follow_up",
  PRACTICE_ADMINISTRATION: "practice_administration",
  VERIFICATION_REQUEST: "verification_request",
  PRESCRIPTION_SUPPORT: "prescription_support",
  DOCUMENT_REVIEW: "document_review",
  INTERNAL_PRACTICE_TASK: "internal_practice_task",
};

export const PRACTICE_TEAM_DELEGATED_TASK_TYPE_LABELS = {
  [PRACTICE_TEAM_DELEGATED_TASK_TYPES.APPOINTMENT_COORDINATION]: "Appointment coordination",
  [PRACTICE_TEAM_DELEGATED_TASK_TYPES.PATIENT_COMMUNICATION]: "Patient communication",
  [PRACTICE_TEAM_DELEGATED_TASK_TYPES.BILLING_FOLLOW_UP]: "Billing follow-up",
  [PRACTICE_TEAM_DELEGATED_TASK_TYPES.PRACTICE_ADMINISTRATION]: "Practice administration",
  [PRACTICE_TEAM_DELEGATED_TASK_TYPES.VERIFICATION_REQUEST]: "Verification request",
  [PRACTICE_TEAM_DELEGATED_TASK_TYPES.PRESCRIPTION_SUPPORT]: "Prescription support",
  [PRACTICE_TEAM_DELEGATED_TASK_TYPES.DOCUMENT_REVIEW]: "Document review",
  [PRACTICE_TEAM_DELEGATED_TASK_TYPES.INTERNAL_PRACTICE_TASK]: "Internal practice task",
};

export const PRACTICE_TEAM_DELEGATED_TASK_MODEL_FIELDS = [
  "id",
  "taskType",
  "priority",
  "status",
  "section",
  "assignedMember",
  "originatingClinician",
  "originatingWorkflow",
  "patientReference",
  "dueDate",
  "createdDate",
  "completedDate",
  "permissionRequired",
];

const DEMO_DELEGATED_TASKS = [
  {
    id: "pt-task-practice-operations-review",
    taskType: PRACTICE_TEAM_DELEGATED_TASK_TYPES.PRACTICE_ADMINISTRATION,
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.HIGH,
    status: PRACTICE_TEAM_DELEGATED_TASK_STATUSES.OPEN,
    section: "today",
    title: "Review practice operations",
    summary: "Prepare the administrative work queue for the practice before delegated workflows are enabled.",
    originatingWorkflow: "Practice administration",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.PRACTICE_OPERATIONS_VIEW,
    dueDate: "Today",
    createdDate: "Today",
    patientReference: null,
  },
  {
    id: "pt-task-appointment-coordination-setup",
    taskType: PRACTICE_TEAM_DELEGATED_TASK_TYPES.APPOINTMENT_COORDINATION,
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.NORMAL,
    status: PRACTICE_TEAM_DELEGATED_TASK_STATUSES.SCHEDULED,
    section: "upcoming",
    title: "Prepare appointment coordination",
    summary: "Appointment coordination tasks will appear here once the practice enables delegated appointment workflows.",
    originatingWorkflow: "Appointments",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_VIEW,
    dueDate: "Upcoming",
    createdDate: "Today",
    patientReference: null,
  },
  {
    id: "pt-task-assignment-confirmed",
    taskType: PRACTICE_TEAM_DELEGATED_TASK_TYPES.INTERNAL_PRACTICE_TASK,
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.LOW,
    status: PRACTICE_TEAM_DELEGATED_TASK_STATUSES.COMPLETED,
    section: "completed",
    title: "Confirm active Practice Team assignment",
    summary: "Member identity, assigned role and active status were confirmed for this workspace shell.",
    originatingWorkflow: "Workspace setup",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.WORKSPACE_HOME_VIEW,
    dueDate: "Today",
    createdDate: "Today",
    completedDate: "Today",
    patientReference: null,
  },
  {
    id: "pt-task-reception-appointment-confirmations",
    taskType: PRACTICE_TEAM_DELEGATED_TASK_TYPES.APPOINTMENT_COORDINATION,
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.HIGH,
    status: PRACTICE_TEAM_DELEGATED_TASK_STATUSES.OPEN,
    section: "today",
    title: "Prepare appointment confirmations",
    summary: "Receptionist coordination tasks will be generated from appointment workflows in a later slice.",
    originatingWorkflow: "Appointments",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_EDIT,
    dueDate: "Today",
    createdDate: "Today",
    patientReference: null,
  },
  {
    id: "pt-task-billing-follow-up",
    taskType: PRACTICE_TEAM_DELEGATED_TASK_TYPES.BILLING_FOLLOW_UP,
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.NORMAL,
    status: PRACTICE_TEAM_DELEGATED_TASK_STATUSES.OPEN,
    section: "today",
    title: "Review billing follow-up queue",
    summary: "Billing follow-up work will appear here once payment workflows are delegated.",
    originatingWorkflow: "Billing",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.BILLING_FOLLOW_UP,
    dueDate: "Today",
    createdDate: "Today",
    patientReference: null,
  },
];

const DEMO_DELEGATED_MESSAGES = [
  {
    id: "pt-message-new-admin-request",
    section: "new_patient_messages",
    messageType: "Administrative patient message",
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.HIGH,
    status: PRACTICE_TEAM_DELEGATED_MESSAGE_STATUSES.NEW,
    title: "Review delegated patient message placeholder",
    summary: "Patient communication will appear here once delegated messaging is enabled.",
    context: "Patient context hidden until messaging permissions are live",
    workflow: "Messages",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.MESSAGES_VIEW_ADMIN,
    state: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
    readOnly: true,
  },
  {
    id: "pt-message-follow-up",
    section: "follow_up_required",
    messageType: "Follow-up message",
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.NORMAL,
    status: PRACTICE_TEAM_DELEGATED_MESSAGE_STATUSES.FOLLOW_UP,
    title: "Prepare patient follow-up queue",
    summary: "Administrative follow-up will be generated from approved delegated messaging workflows later.",
    context: "Practice-level placeholder",
    workflow: "Patient communication",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.MESSAGES_CREATE_ADMIN,
    state: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
    readOnly: true,
  },
  {
    id: "pt-message-internal-note",
    section: "internal_practice_notes",
    messageType: "Internal practice note",
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.LOW,
    status: PRACTICE_TEAM_DELEGATED_MESSAGE_STATUSES.INTERNAL,
    title: "Review practice coordination note placeholder",
    summary: "Internal notes will support practice administration once delegated workflows mature.",
    context: "Assigned practice only",
    workflow: "Practice administration",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.PRACTICE_OPERATIONS_VIEW,
    state: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
    readOnly: true,
  },
  {
    id: "pt-message-archived",
    section: "completed_archived",
    messageType: "Archived delegated message",
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.LOW,
    status: PRACTICE_TEAM_DELEGATED_MESSAGE_STATUSES.ARCHIVED,
    title: "Confirmed messaging area remains read-only",
    summary: "This placeholder confirms that no live patient communication is enabled in this slice.",
    context: "Workspace proof",
    workflow: "Messages setup",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.MESSAGES_VIEW_ADMIN,
    state: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
    readOnly: true,
  },
];

const DEMO_PATIENT_COORDINATION_ITEMS = [
  {
    id: "pt-patient-new-registration-readiness",
    section: "new_registrations",
    coordinationType: "Registration coordination",
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.NORMAL,
    status: PRACTICE_TEAM_PATIENT_COORDINATION_STATUSES.NEW_REGISTRATION,
    title: "Prepare new registration queue",
    summary: "New patient registration coordination will appear here once delegated patient workflows are enabled.",
    context: "Patient identity withheld until permissions are live",
    workflow: "Patient coordination",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED,
    state: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
    readOnly: true,
  },
  {
    id: "pt-patient-verification-readiness",
    section: "awaiting_verification",
    coordinationType: "Verification readiness",
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.HIGH,
    status: PRACTICE_TEAM_PATIENT_COORDINATION_STATUSES.AWAITING_VERIFICATION,
    title: "Prepare verification follow-up placeholder",
    summary: "Non-clinical verification follow-up will be generated from patient coordination workflows in a later slice.",
    context: "No patient records or clinical details shown",
    workflow: "Verification support",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED,
    state: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
    readOnly: true,
  },
  {
    id: "pt-patient-profile-follow-up",
    section: "follow_up_required",
    coordinationType: "Profile follow-up",
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.NORMAL,
    status: PRACTICE_TEAM_PATIENT_COORDINATION_STATUSES.INCOMPLETE_PROFILE,
    title: "Review incomplete patient profile queue",
    summary: "Administrative profile follow-up placeholders will appear here without exposing clinical information.",
    context: "Practice-level placeholder",
    workflow: "Patient profile readiness",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED,
    state: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
    readOnly: true,
  },
  {
    id: "pt-patient-booking-preparation",
    section: "booking_preparation",
    coordinationType: "Booking preparation",
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.LOW,
    status: PRACTICE_TEAM_PATIENT_COORDINATION_STATUSES.READY_FOR_BOOKING,
    title: "Prepare booking readiness placeholder",
    summary: "Booking preparation work will appear here after appointment and patient coordination capabilities are enabled.",
    context: "Assigned practice only",
    workflow: "Booking preparation",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED,
    state: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
    readOnly: true,
  },
  {
    id: "pt-patient-archived-proof",
    section: "completed_archived",
    coordinationType: "Archived patient coordination",
    priority: PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.LOW,
    status: "read-only",
    title: "Confirmed patient coordination remains non-clinical",
    summary: "This placeholder confirms the Patients surface is a coordination workspace, not patient records.",
    context: "Workspace proof",
    workflow: "Patient coordination setup",
    permissionRequired: PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED,
    state: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
    readOnly: true,
  },
];

export const PRACTICE_TEAM_CAPABILITY_AREAS = [
  {
    id: "assignment",
    label: "Assignment",
    capability: PRACTICE_TEAM_CAPABILITIES.WORKSPACE_HOME_VIEW,
    stateWhenAllowed: PRACTICE_TEAM_CAPABILITY_STATES.AVAILABLE,
  },
  {
    id: "tasks",
    label: "My Tasks",
    capability: PRACTICE_TEAM_CAPABILITIES.TASKS_VIEW,
    stateWhenAllowed: PRACTICE_TEAM_CAPABILITY_STATES.AVAILABLE,
  },
  {
    id: "appointments",
    label: "Appointments",
    capability: PRACTICE_TEAM_CAPABILITIES.APPOINTMENTS_VIEW,
    stateWhenAllowed: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
  },
  {
    id: "messages",
    label: "Messages",
    capability: PRACTICE_TEAM_CAPABILITIES.MESSAGES_VIEW_ADMIN,
    stateWhenAllowed: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
  },
  {
    id: "patients",
    label: "Patient contacts",
    capability: PRACTICE_TEAM_CAPABILITIES.PATIENT_CONTACT_VIEW_LIMITED,
    stateWhenAllowed: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
  },
  {
    id: "billing",
    label: "Billing follow-up",
    capability: PRACTICE_TEAM_CAPABILITIES.BILLING_STATUS_VIEW,
    stateWhenAllowed: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
  },
  {
    id: "practice",
    label: "Practice administration",
    capability: PRACTICE_TEAM_CAPABILITIES.PRACTICE_OPERATIONS_VIEW,
    stateWhenAllowed: PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION,
  },
  {
    id: "patient_records",
    label: "Patient records",
    capability: "clinical.patient_records.view",
    stateWhenDenied: PRACTICE_TEAM_CAPABILITY_STATES.LOCKED,
  },
  {
    id: "clinical_notes",
    label: "Clinical notes",
    capability: "clinical.notes.view",
    stateWhenDenied: PRACTICE_TEAM_CAPABILITY_STATES.LOCKED,
  },
  {
    id: "prescriptions",
    label: "Prescriptions",
    capability: "clinical.prescriptions.manage",
    stateWhenDenied: PRACTICE_TEAM_CAPABILITY_STATES.LOCKED,
  },
  {
    id: "vitalrep",
    label: "VitalRep™",
    capability: "clinical.vitalrep.manage",
    stateWhenDenied: PRACTICE_TEAM_CAPABILITY_STATES.LOCKED,
  },
  {
    id: "data_exports",
    label: "Data exports",
    capability: "data.exports.create",
    stateWhenDenied: PRACTICE_TEAM_CAPABILITY_STATES.LOCKED,
  },
  {
    id: "clinician_settings",
    label: "Clinician settings",
    capability: "clinician.settings.configure",
    stateWhenDenied: PRACTICE_TEAM_CAPABILITY_STATES.LOCKED,
  },
];

const MATRIX = {
  practice_assistant: {
    view: ["workspace", "tasks", "appointments"],
    create: [],
    edit: [],
    delete: [],
    approve: [],
    export: [],
    invite: [],
    configure: [],
  },
  receptionist: {
    view: ["workspace", "tasks", "appointments", "messages", "limited_patient_contacts"],
    create: ["appointments", "administrative_messages"],
    edit: ["appointments"],
    delete: [],
    approve: [],
    export: [],
    invite: [],
    configure: [],
  },
  practice_manager: {
    view: ["workspace", "tasks", "appointments", "messages", "limited_patient_contacts", "practice_operations"],
    create: ["appointments", "administrative_messages", "operational_tasks"],
    edit: ["appointments", "practice_operations"],
    delete: [],
    approve: ["administrative_tasks"],
    export: [],
    invite: ["limited_team_invites"],
    configure: ["limited_team_settings"],
  },
  billing_administrator: {
    view: ["workspace", "tasks", "billing_status"],
    create: ["billing_follow_up_tasks"],
    edit: ["billing_follow_up_tasks"],
    delete: [],
    approve: [],
    export: [],
    invite: [],
    configure: [],
  },
};

export function normalizePracticeTeamRole(role = "") {
  const normalized = String(role || "").trim().toLowerCase();
  return ROLE_SEQUENCE.includes(normalized) ? normalized : "practice_assistant";
}

export function getPracticeTeamRoleLabel(role = "") {
  const normalized = normalizePracticeTeamRole(role);
  return PRACTICE_TEAM_ROLES.find((item) => item.value === normalized)?.label || "Practice Assistant";
}

export function getPracticeTeamRoleDescription(role = "") {
  const normalized = normalizePracticeTeamRole(role);
  return PRACTICE_TEAM_ROLES.find((item) => item.value === normalized)?.description || "";
}

export function getPracticeTeamCapabilities(role = "") {
  return new Set(ROLE_CAPABILITIES[normalizePracticeTeamRole(role)] || ROLE_CAPABILITIES.practice_assistant);
}

export function getPracticeTeamNavigation(role = "") {
  const capabilities = getPracticeTeamCapabilities(role);
  return PRACTICE_TEAM_NAVIGATION_SECTIONS.map((section) => ({
    ...section,
    enabled: section.routeImplemented && capabilities.has(section.capability),
    state: !section.routeImplemented
      ? PRACTICE_TEAM_CAPABILITY_STATES.PENDING_VERIFICATION
      : capabilities.has(section.capability)
        ? PRACTICE_TEAM_CAPABILITY_STATES.AVAILABLE
        : PRACTICE_TEAM_CAPABILITY_STATES.PERMISSION_DENIED,
  }));
}

export function canManagePracticeTeam(role = "") {
  return getPracticeTeamCapabilities(role).has(PRACTICE_TEAM_CAPABILITIES.MANAGE_PRACTICE_TEAM);
}

export function getPracticeTeamModuleTruth(role = "") {
  const navigationById = new Map(getPracticeTeamNavigation(role).map((item) => [item.id, item]));
  return [
    { id: "tasks", label: "My Tasks", route: "/practice-team/tasks" },
    { id: "appointments", label: "Appointments", route: "/practice-team/appointments" },
    { id: "messages", label: "Messages", route: "/practice-team/messages" },
    { id: "patients", label: "Patients", route: "/practice-team/patients" },
    { id: "billing", label: "Billing", route: "/practice-team/billing" },
    { id: "practice", label: "Practice", route: "/practice-team/practice" },
    { id: "notifications", label: "Notifications", route: "/practice-team/notifications" },
  ].map((module) => {
    const navigation = navigationById.get(module.id);
    return {
      ...module,
      routeImplemented: Boolean(navigation?.routeImplemented),
      permitted: Boolean(navigation?.enabled),
      state: navigation?.state || PRACTICE_TEAM_CAPABILITY_STATES.PERMISSION_DENIED,
    };
  });
}

export function getPracticeTeamNavigationSection(role = "", sectionId = "") {
  return getPracticeTeamNavigation(role).find((section) => section.id === sectionId) || null;
}

export function getPracticeTeamTasks(role = "") {
  return PRACTICE_TEAM_TASK_GUIDE[normalizePracticeTeamRole(role)] || PRACTICE_TEAM_TASK_GUIDE.practice_assistant;
}

export function getPracticeTeamPriorityMeta(priority = "") {
  return (
    PRACTICE_TEAM_PRIORITY_META[priority] ||
    PRACTICE_TEAM_PRIORITY_META[PRACTICE_TEAM_DELEGATED_TASK_PRIORITIES.NORMAL]
  );
}

export function getPracticeTeamTaskTypeLabel(taskType = "") {
  return PRACTICE_TEAM_DELEGATED_TASK_TYPE_LABELS[taskType] || "Delegated task";
}

export function getPracticeTeamDelegatedTasks(role = "", memberIdentity = {}) {
  const capabilities = getPracticeTeamCapabilities(role);
  const clinicianName = memberIdentity?.practiceName || "Assigned clinician";
  const assignedMember = memberIdentity?.displayName || "Practice Team member";

  return DEMO_DELEGATED_TASKS.filter((task) => capabilities.has(task.permissionRequired)).map((task) => ({
    ...task,
    assignedMember,
    originatingClinician: clinicianName,
  }));
}

export function getPracticeTeamTaskSections(tasks = []) {
  return PRACTICE_TEAM_DELEGATED_TASK_SECTIONS.map((section) => ({
    ...section,
    tasks: tasks.filter((task) => task.section === section.id),
  }));
}

export function getPracticeTeamDelegatedMessages(role = "") {
  const capabilities = getPracticeTeamCapabilities(role);

  return DEMO_DELEGATED_MESSAGES.filter((message) => capabilities.has(message.permissionRequired));
}

export function getPracticeTeamMessageSections(messages = []) {
  return PRACTICE_TEAM_DELEGATED_MESSAGE_SECTIONS.map((section) => ({
    ...section,
    messages: messages.filter((message) => message.section === section.id),
  }));
}

export function getPracticeTeamPatientCoordinationItems(role = "") {
  const capabilities = getPracticeTeamCapabilities(role);

  return DEMO_PATIENT_COORDINATION_ITEMS.filter((item) => capabilities.has(item.permissionRequired));
}

export function getPracticeTeamPatientCoordinationSections(items = []) {
  return PRACTICE_TEAM_PATIENT_COORDINATION_SECTIONS.map((section) => ({
    ...section,
    items: items.filter((item) => item.section === section.id),
  }));
}

export function getPracticeTeamCapabilityAreas(role = "") {
  const capabilities = getPracticeTeamCapabilities(role);
  return PRACTICE_TEAM_CAPABILITY_AREAS.map((area) => {
    const allowed = capabilities.has(area.capability);
    return {
      ...area,
      state: allowed
        ? area.stateWhenAllowed || PRACTICE_TEAM_CAPABILITY_STATES.AVAILABLE
        : area.stateWhenDenied || PRACTICE_TEAM_CAPABILITY_STATES.PERMISSION_DENIED,
    };
  });
}

export function getPracticeTeamUnavailableCapabilityAreas(role = "", { exclude = [] } = {}) {
  const excluded = new Set(exclude);
  return getPracticeTeamCapabilityAreas(role).filter((area) => {
    if (excluded.has(area.id)) return false;
    return area.state !== PRACTICE_TEAM_CAPABILITY_STATES.AVAILABLE;
  });
}

export function getPracticeTeamRoleMatrix(role = "") {
  return MATRIX[normalizePracticeTeamRole(role)] || MATRIX.practice_assistant;
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function resolveOfficialClinicianName(invite = {}) {
  const title = firstText(invite.doctor_title, invite.clinician_title, invite.title);
  const familyName = firstText(
    invite.doctor_family_name,
    invite.doctor_last_name,
    invite.clinician_family_name,
    invite.clinician_last_name,
  );
  if (title && familyName) return `${title} ${familyName}`;
  if (familyName) return `Dr. ${familyName}`;

  const explicitDisplay = firstText(
    invite.doctor_display_name,
    invite.clinician_display_name,
    invite.doctor_official_name,
    invite.clinician_official_name,
  );
  if (explicitDisplay) return explicitDisplay;

  const rawName = firstText(invite.doctor_name, invite.clinician_name);
  const parts = rawName.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `Dr. ${parts[parts.length - 1]}`;
  return rawName;
}

function resolvePracticeTeamAssignmentName(invite = {}) {
  const practiceName = firstText(
    invite.practice_name,
    invite.practice_display_name,
    invite.clinic_name,
    invite.facility_name,
  );
  const rawClinicianName = firstText(invite.doctor_name, invite.clinician_name);
  const practiceIsRawClinicianName =
    practiceName && rawClinicianName && practiceName.toLowerCase() === rawClinicianName.toLowerCase();
  if (practiceName && !practiceIsRawClinicianName) {
    return {
      value: practiceName,
      source: "practice_name",
    };
  }

  const clinicianName = resolveOfficialClinicianName(invite);
  return {
    value: clinicianName,
    source: clinicianName ? "clinician_official_name_fallback" : "unavailable",
  };
}

export function getPracticeTeamMemberIdentity(invite = {}, user = {}) {
  invite = invite || {};
  user = user || {};
  const assignmentName = resolvePracticeTeamAssignmentName(invite);
  const fullName =
    [invite.first_name, invite.family_name].filter(Boolean).join(" ") ||
    invite.full_name ||
    invite.member_name ||
    user?.user_metadata?.full_name ||
    invite.email ||
    "Practice Team member";
  const parts = String(fullName || invite.email || "").trim().split(/\s+/).filter(Boolean);
  const initials = parts.length > 1
    ? `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase()
    : String(parts[0] || "PT").slice(0, 2).toUpperCase();

  return {
    displayName: fullName,
    email: invite.email || user?.email || "",
    phone: invite.phone || invite.phone_number || "",
    avatarUrl: invite.avatar_url || invite.profile_image_url || "",
    initials: initials || "PT",
    verificationState: invite.verification_state || invite.status || "active",
    practiceName: assignmentName.value,
    practiceNameSource: assignmentName.source,
  };
}
